"use client";

// ReorderableChips — the view-chip bar with two gestures on one control:
//   • TAP a chip          → select that view.
//   • PRESS-AND-HOLD then  → enter rearrange mode and drag left/right; the new
//     drag                   order persists, and the LEFTMOST chip becomes the
//                            default-selected view for this level.
//
// Implemented with pointer events so it works the same with touch and mouse.
// During a drag the DOM order is left untouched: the dragged chip follows the
// finger, and the chips it passes shift by exactly the dragged chip's footprint
// to open a drop slot — so reflow is correct even though chips differ in width.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { ChipDef } from "./OsNavigator";
import { useChipRow } from "./chipRow";

const HOLD_MS = 480; // a deliberate press, not a tap
const MOVE_CANCEL = 8; // px of travel before the hold fires → it's a scroll/tap
const GAP = 9.6; // only when the strip's own gap cannot be read: --chip-gap, 0.6rem (begin() reads the real one)

type Drag = { id: string; from: number; to: number; dx: number; gw: number };

export default function ReorderableChips({
  chips,
  view,
  onView,
  onNewView,
  onReorder,
  allowNew = true,
  renderAccessory,
  unseen,
  trailing,
  onChipContextMenu,
}: {
  chips: ChipDef[];
  view: string;
  onView: (id: string) => void;
  onNewView: () => void;
  onReorder: (ids: string[]) => void;
  /** show the "+ new view" affordance (false for fixed view-sets like /admin). */
  allowNew?: boolean;
  /** optional trailing element rendered inside a chip (e.g. the orbit dial). */
  renderAccessory?: (id: string) => ReactNode;
  /** view ids not yet opened — they carry a slight shimmer until first opened
   *  (the onboarding "reveal", law_sequential_unfold). */
  unseen?: Set<string>;
  /** an element seated at the END of the SAME chip row, pushed right. This is
   *  how a view that owns sub-views (a directory's desktop/grid/list) offers
   *  them WITHOUT opening a second chip row beneath — one navigator, one row
   *  (law_one_chip_row). It never enters the drag order. */
  trailing?: ReactNode;
  /** right-click / long-press on a chip — a view someone MADE can be let go of
   *  from here. The level decides which chips are lettable. */
  onChipContextMenu?: (id: string) => void;
}) {
  const stripRef = useRef<HTMLDivElement | null>(null);
  // the strip is the one view-chip row (globals.css .nav-chips): it fades into the edge it
  // continues past and keeps the chip that is on in reach (components/os/chipRow.ts)
  const watch = useChipRow();
  const seatStrip = useCallback(
    (el: HTMLDivElement | null) => {
      stripRef.current = el;
      watch(el);
    },
    [watch],
  );
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [drag, setDrag] = useState<Drag | null>(null);
  const editing = drag !== null;
  const ids = chips.map((c) => c.id);

  const g = useRef<{
    id: string;
    sx: number;
    sy: number;
    timer: ReturnType<typeof setTimeout> | null;
    active: boolean;
    /** the press travelled past MOVE_CANCEL before it became a hold: a swipe or a
     *  scroll that happened to begin on a chip, never a tap on it */
    moved: boolean;
    rects: { center: number; width: number }[];
    from: number;
  } | null>(null);

  // a hold still waiting on its timer is let go of; one already rearranging is not
  const cancelPending = () => {
    const gg = g.current;
    if (!gg || gg.active) return;
    if (gg.timer) clearTimeout(gg.timer);
    g.current = null;
  };
  // nothing outlives the bar: a timer left running would begin a drag on a chip
  // that is no longer there
  useEffect(() => () => { if (g.current?.timer) clearTimeout(g.current.timer); }, []);

  // A finger that has held long enough to rearrange must not ALSO pan the strip.
  // touch-action can't be changed mid-gesture, so once a drag is live the move is
  // taken back here; React's own touchmove is passive, hence the native listener.
  const hasTrailing = trailing != null;
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const hold = (e: TouchEvent) => { if (g.current?.active && e.cancelable) e.preventDefault(); };
    el.addEventListener("touchmove", hold, { passive: false });
    return () => el.removeEventListener("touchmove", hold);
  }, [hasTrailing]);

  const begin = (id: string) => {
    const strip = stripRef.current;
    const gg = g.current;
    if (!strip || !gg) return;
    const base = strip.getBoundingClientRect();
    gg.timer = null; // it has fired
    // the chips can change inside the hold window (a view made or let go of);
    // a chip that is gone means there is nothing sound to measure, so no drag
    const rects: { center: number; width: number }[] = [];
    for (const cid of ids) {
      const el = itemRefs.current[cid];
      if (!el) return; // the press goes on as a plain tap
      const r = el.getBoundingClientRect();
      rects.push({ center: r.left - base.left + r.width / 2, width: r.width });
    }
    const from = ids.indexOf(id);
    if (from < 0) return;
    gg.rects = rects;
    gg.from = from;
    gg.active = true;
    // the real gap between chips, so a slot opens exactly as wide as the chip it was left by
    const gap = parseFloat(getComputedStyle(strip).columnGap);
    setDrag({ id, from, to: from, dx: 0, gw: rects[from].width + (Number.isFinite(gap) ? gap : GAP) });
  };

  const onDown = (e: React.PointerEvent, id: string) => {
    // the primary press only: a right-click or a second finger is not a hold.
    // Nor is a ctrl-click (a context click on a mac): the menu that follows it
    // swallows the pointerup that would have ended the hold.
    if (e.button !== 0 || !e.isPrimary) return;
    const reorderable = chips.length > 1 && !e.ctrlKey; // one chip has nothing to reorder
    if (reorderable) {
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* best effort */ }
    }
    const timer = reorderable ? setTimeout(() => begin(id), HOLD_MS) : null;
    g.current = { id, sx: e.clientX, sy: e.clientY, timer, active: false, moved: false, rects: [], from: -1 };
  };

  const onMove = (e: React.PointerEvent) => {
    const gg = g.current;
    if (!gg) return;
    if (!gg.active) {
      if (!gg.moved && (Math.abs(e.clientX - gg.sx) > MOVE_CANCEL || Math.abs(e.clientY - gg.sy) > MOVE_CANCEL)) {
        // a scroll, a swipe, or a tap-with-travel: not a hold, and not a tap either
        if (gg.timer) clearTimeout(gg.timer);
        gg.timer = null;
        gg.moved = true;
      }
      return;
    }
    e.preventDefault();
    const dx = e.clientX - gg.sx;
    const center = gg.rects[gg.from].center + dx;
    let to = 0;
    for (let i = 0; i < gg.rects.length; i++) {
      if (i === gg.from) continue;
      if (gg.rects[i].center < center) to++;
    }
    setDrag((d) => (d ? { ...d, dx, to } : d));
  };

  const finish = (): boolean => {
    const gg = g.current;
    g.current = null;
    if (!gg) {
      setDrag(null);
      return false;
    }
    if (gg.timer) clearTimeout(gg.timer);
    if (!gg.active) {
      setDrag(null);
      return false; // never entered drag → caller treats as a tap
    }
    setDrag((d) => {
      if (d && d.to !== d.from) {
        const next = ids.slice();
        const [m] = next.splice(d.from, 1);
        next.splice(d.to, 0, m);
        onReorder(next);
      }
      return null;
    });
    return true;
  };

  // The pointer's capture left the chip. The shell's swipe captures every press on
  // whatever was pressed, which is usually the label INSIDE the chip: the chip loses
  // it and the press goes on, so that is not an end. A capture that is really gone
  // (the browser took it back, the chip unmounted) ends the hold, or the rearrange.
  const onLost = (e: React.PointerEvent) => {
    if (!g.current) return;
    const chip = e.currentTarget as HTMLElement;
    const holds = (n: Element) => n.hasPointerCapture?.(e.pointerId) === true;
    if (holds(chip) || Array.from(chip.querySelectorAll("*")).some(holds)) return;
    if (g.current.active) finish();
    else cancelPending();
  };

  const onUp = (e: React.PointerEvent, id: string) => {
    if (!e.isPrimary) return;
    const travelled = g.current?.moved ?? false;
    const pressed = g.current !== null;
    if (finish()) return; // it was a rearrange
    // a tap selects. A swipe that began on this chip does not, and neither does a
    // release whose press we never took (a right-click, a menu in between)
    if (pressed && !travelled) onView(id);
  };

  const transformFor = (i: number): string => {
    if (!drag) return "none";
    if (i === drag.from) return `translateX(${drag.dx}px) scale(1.06)`;
    if (drag.to > drag.from && i > drag.from && i <= drag.to) return `translateX(${-drag.gw}px)`;
    if (drag.to < drag.from && i >= drag.to && i < drag.from) return `translateX(${drag.gw}px)`;
    return "translateX(0px)";
  };

  const strip = (
    <div ref={seatStrip} className="os-chips nav-chips" data-hscroll data-reorder={editing || undefined}>
      {chips.map((c, i) => {
        const isDrag = drag?.id === c.id;
        return (
          <button
            key={c.id}
            ref={(el) => {
              itemRefs.current[c.id] = el;
            }}
            className="chip"
            data-on={view === c.id}
            data-dragging={isDrag || undefined}
            data-unseen={unseen?.has(c.id) || undefined}
            data-noswipe
            data-hint-key={`view.${c.id}`}
            onPointerDown={(e) => onDown(e, c.id)}
            onPointerMove={onMove}
            onPointerUp={(e) => onUp(e, c.id)}
            onPointerCancel={() => finish()}
            // the hold's timer must not outlive the press it belongs to: when the
            // browser takes the capture back, a menu opens, or the chip is gone
            onLostPointerCapture={onLost}
            onContextMenu={(e) => {
              cancelPending(); // a menu is not a hold (the mac's ctrl-click ate the pointerup and left a drag stuck)
              if (onChipContextMenu) { e.preventDefault(); onChipContextMenu(c.id); }
              else if (g.current?.active) e.preventDefault(); // no native menu over a rearrange
            }}
            style={{
              transform: transformFor(i),
              transition: isDrag ? "none" : "transform 0.2s var(--ease-soft)",
              zIndex: isDrag ? 3 : 1,
              position: "relative",
              boxShadow: isDrag ? "var(--shadow-lift)" : undefined,
              cursor: editing ? (isDrag ? "grabbing" : "grab") : "pointer",
              // both axes stay the browser's: a finger on a chip can pan the strip
              // sideways (with seven or eight chips on a phone, a chip is most of
              // what there is to touch) or scroll the page. The one thing the page
              // takes back is a LIVE rearrange (the touchmove hold above).
              touchAction: "pan-x pan-y",
              userSelect: "none",
              WebkitUserSelect: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <span className={unseen?.has(c.id) ? "chip-unseen-label" : undefined}>{c.label}</span>
            {renderAccessory?.(c.id)}
          </button>
        );
      })}
      {allowNew && (
        <button className="chip chip-plus" onClick={onNewView} data-noswipe data-hint-key="nav.chip.new">
          + new view
        </button>
      )}
    </div>
  );

  // A view that owns sub-views hangs them at the END of the same row, in their
  // own group OUTSIDE the scroller — so the sections scroll under the pointer
  // while the ways of looking stay exactly where you left them. One row, two
  // clusters; never a second row of chips (law_one_chip_row).
  if (!trailing) return strip;
  return (
    <div className="os-chiprow">
      {strip}
      <span className="chips-trailing" data-noswipe>
        {trailing}
      </span>
    </div>
  );
}
